/**
 * Pure view-model helpers for the Taxonomy screen: category origin, the example
 * summary line for a suggestion card, and small pluralisers. Kept free of React
 * so the numbers and strings are unit-testable on their own.
 */
import type { CategorySuggestion, EffectiveCategory } from '../../api/schema';

export type CategoryOrigin = 'Base' | 'System' | 'Custom';

/** The system fallback bucket every tenant carries (design-reference §4.7). */
export function isSystemFallback(category: Pick<EffectiveCategory, 'name'>): boolean {
  return category.name.trim().toLowerCase() === 'other';
}

/**
 * Where a category came from: the `Other` fallback is `System` (its toggle is
 * refused), a seeded category is `Base`, anything a tenant added is `Custom`.
 */
export function categoryOrigin(category: Pick<EffectiveCategory, 'name' | 'is_base'>): CategoryOrigin {
  if (isSystemFallback(category)) return 'System';
  return category.is_base ? 'Base' : 'Custom';
}

/** `1 document` · `37 documents`. */
export function documentsLabel(n: number): string {
  return `${n.toLocaleString('en-GB')} document${n === 1 ? '' : 's'}`;
}

const EXAMPLE_LIMIT = 2;

/**
 * The mono example line on a suggestion card. Shows up to two of the grouped
 * document ids, then `+N more` where N accounts for the full occurrence count —
 * so a group of 37 seen through two example ids reads `… +35 more`.
 */
export function exampleSummary(suggestion: Pick<CategorySuggestion, 'example_document_ids' | 'occurrence_count'>): string {
  const ids = suggestion.example_document_ids ?? [];
  const total = Math.max(suggestion.occurrence_count ?? ids.length, ids.length);
  if (ids.length === 0) {
    return total > 0 ? `${documentsLabel(total)} matched — ids pending` : 'no example documents yet';
  }
  const shown = ids.slice(0, EXAMPLE_LIMIT);
  const remaining = total - shown.length;
  const head = shown.join(' · ');
  return remaining > 0 ? `${head} +${remaining.toLocaleString('en-GB')} more` : head;
}

/**
 * The disable-impact clause for the toggle toast. When the API reports documents
 * still carry the category, the toast names the count so the effect is shown, not
 * discovered later.
 */
export function disableToast(name: string, impact: number | null | undefined): string {
  if (impact != null && impact > 0) {
    return `"${name}" disabled — ${documentsLabel(impact)} keep their class; new lookalikes route to review`;
  }
  return `"${name}" disabled — new matches will route to review`;
}

// -------------------------------------------------------- existing-category match --
// Near-duplicate PROPOSALS (e.g. "Bank Statement / Account Statement" vs "Bank
// Transaction History / Account Statement") are folded together automatically
// at intake time (`app.services.review_intake`, backend) — before a second
// pending suggestion ever exists, so there is nothing left for the frontend to
// flag or merge. A match against an EXISTING taxonomy category (typically one
// the tenant disabled) is still a frontend-visible signal, since that decision
// — re-enable a category the tenant deliberately turned off — stays a
// deliberate, one-click human action rather than something the pipeline
// resolves silently on its own.

/** Connectors dropped before comparing names — real domain words like
 * "statement"/"bill" are kept; they carry the actual discriminating signal. */
const STOPWORDS = new Set(['a', 'an', 'the', 'and', 'or', 'of', 'for', 'to', 'in', 'on', 'with']);

/** Lowercased, punctuation-split, stopword-free tokens of a name. */
function significantTokens(name: string): Set<string> {
  return new Set(
    name
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length > 0 && !STOPWORDS.has(token)),
  );
}

/** Jaccard overlap of two names' significant tokens, 0..1. Mirrors
 * `app.services.suggestion_matching.token_overlap_ratio` (backend) exactly —
 * the backend independently re-derives the same match at click time rather
 * than trusting this render-time decision, so the two must agree. */
function tokenOverlapRatio(a: string, b: string): number {
  const tokensA = significantTokens(a);
  const tokensB = significantTokens(b);
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let intersection = 0;
  for (const token of tokensA) if (tokensB.has(token)) intersection += 1;
  const union = tokensA.size + tokensB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

/** Above this score, a suggestion likely paraphrases an existing category
 * (e.g. "Utility Bill / Invoice" for an existing "Utility Bill" scores 0.67). */
const CATEGORY_MATCH_THRESHOLD = 0.5;

/** A 0..1 confidence that `a`/`b` name the same real document type: the higher
 * of the plain token-Jaccard overlap, or 1 when one name's *entire* token set
 * is contained in the other's — e.g. "Invoice" is fully contained in "Invoice
 * / Billing Statement", the same real type proposed with an added qualifier,
 * even though plain Jaccard alone scores this pair only 0.33 (a short name's
 * tokens are a small fraction of the union with a much longer name). Mirrors
 * `app.services.suggestion_matching.match_score` (backend) exactly — the
 * backend independently re-derives the same match at click time rather than
 * trusting this render-time decision, so the two must agree. */
function matchScore(a: string, b: string): number {
  const ratio = tokenOverlapRatio(a, b);
  const tokensA = significantTokens(a);
  const tokensB = significantTokens(b);
  if (tokensA.size === 0 || tokensB.size === 0) return ratio;
  const isSubset = (small: Set<string>, big: Set<string>): boolean => {
    for (const token of small) if (!big.has(token)) return false;
    return true;
  };
  if (isSubset(tokensA, tokensB) || isSubset(tokensB, tokensA)) return 1;
  return ratio;
}

/** The tenant's category matching this suggested name — first by exact,
 * case-insensitive, trimmed equality; failing that, the best-scoring
 * near-duplicate by `matchScore` (e.g. a suggestion that merely paraphrases
 * an existing category's name). Enabled or disabled. The signal behind the
 * "Turn on & classify" button swap.
 *
 * Ranked by `(matchScore, shared token count)`: full containment can make
 * several categories tie at a score of 1 (e.g. "Utility Bill / Invoice" fully
 * contains both an existing "Utility Bill" *and* an existing "Invoice"), so
 * the raw number of shared tokens breaks the tie in favour of the more
 * specific, closer-worded match. */
export function findMatchingCategory<T extends Pick<EffectiveCategory, 'name'>>(
  suggestedName: string,
  categories: readonly T[],
): T | undefined {
  const normalized = suggestedName.trim().toLowerCase();
  const exact = categories.find((category) => category.name.trim().toLowerCase() === normalized);
  if (exact !== undefined) return exact;

  const nameTokens = significantTokens(suggestedName);
  let best: T | undefined;
  let bestScore = 0;
  let bestOverlap = 0;
  for (const category of categories) {
    const score = matchScore(suggestedName, category.name);
    if (score < CATEGORY_MATCH_THRESHOLD) continue;
    let overlap = 0;
    for (const token of nameTokens) if (significantTokens(category.name).has(token)) overlap += 1;
    if (score > bestScore || (score === bestScore && overlap > bestOverlap)) {
      best = category;
      bestScore = score;
      bestOverlap = overlap;
    }
  }
  return best;
}

/** Button copy for the existing-category-match action — different wording when
 * the match is already enabled, since "Turn on" would falsely claim a state
 * change. */
export function turnOnButtonLabel(matched: Pick<EffectiveCategory, 'enabled'>): string {
  return matched.enabled ? 'Classify as existing' : 'Turn on & classify';
}

/** Tooltip naming the exact existing-category match. Kept short — the shared
 * `Tooltip` popup (`.ds-tooltip`, `max-width: 280px`) is sized for a
 * one-line hint, not a full sentence; a longer message wraps into a tall,
 * narrow column that can visually overlap unrelated content above it. */
export function matchTooltip(matched: Pick<EffectiveCategory, 'name' | 'enabled'>): string {
  return matched.enabled
    ? `Matches "${matched.name}"`
    : `Matches "${matched.name}" (disabled)`;
}
